
import Vue from 'vue'

import { projectClient } from '@/apolloClient'
import {
    CREATE_LOCATION_DRAWING_MUTATION,
    SET_LOCATION_DRAWING_NAME_MUTATION,
    SET_LOCATION_DRAWING_DESCRIPTION_MUTATION,
} from '@/views/Project/gql/mutations'

import DialogTemplate from '@/_components/dialog/template/template.vue'
import RichTextField from '@/components/RichTextField/richTextField.vue'
import Attachment from '@/_components/image/attachment.vue'
import DropZone from '@/components/Picker/DropZone/DropZone.vue'

const LocationDrawingDialog = Vue.extend({
    components: { DialogTemplate, RichTextField, Attachment, DropZone },

    props: {
        props: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            editorId: crypto['randomUUID'](),
            localLocationDrawing: null as null | Record<string, any>,
            loading: false,
            valid: false,
            hasError: false,
            availableFields: {
                name: '',
                description: '',
                file: null as any,
                fileName: '',
            },
            loadingAttachment: false,
        }
    },

    computed: {
        projectId() {
            return this.props.projectId
        },

        requirements(): string {
            return this.props.requirements
        },

        locationDrawing(): any {
            return this.props.locationDrawing
        },

        hasLocationDrawing() {
            return !!this.localLocationDrawing
        },

        isValid() {
            return this.availableFields.file && this.valid
        },
        changedFields() {
            const changedFields = new Set<string>()
            if (this.availableFields.name !== this.localLocationDrawing?.name) {
                changedFields.add('name')
            }

            if (this.availableFields.description !== this.localLocationDrawing?.description) {
                changedFields.add('description')
            }
            return [...changedFields]
        },
    },

    methods: {
        setLoadingAttachment(data: any) {
            this.loadingAttachment = data
        },

        passFiles(file: any) {
            this.availableFields.file = file.attributes.fileId
            this.availableFields.fileName = file.attributes.fileName
        },

        handleLocationDrawing() {
            if (this.localLocationDrawing) {
                this.updateLocationDrawing()
            } else {
                this.createLocationDrawing()
            }
        },

        async updateLocationDrawing() {
            if (!this.localLocationDrawing) return
            const mutations = [] as Array<any>

            if (this.changedFields.includes('name')) {
                mutations.push(
                    projectClient
                        .mutate({
                            mutation: SET_LOCATION_DRAWING_NAME_MUTATION,
                            variables: {
                                id: this.localLocationDrawing.id,
                                name: this.availableFields.name,
                            },
                        })
                        .then((response) => ({
                            type: 'name',
                            data: response.data.setLocationDrawingName.locationDrawing,
                        }))
                )
            }
            if (this.changedFields.includes('description')) {
                mutations.push(
                    projectClient
                        .mutate({
                            mutation: SET_LOCATION_DRAWING_DESCRIPTION_MUTATION,
                            variables: {
                                id: this.localLocationDrawing.id,
                                description: this.availableFields.description,
                            },
                        })
                        .then((response) => ({
                            type: 'description',
                            data: response.data.setLocationDrawingDescription.locationDrawing,
                        }))
                )
            }

            try {
                this.loading = true
                const results = await Promise.all(mutations)
                results.forEach((result) => {
                    if (result.type === 'name') {
                        this.localLocationDrawing.name = result.data.name
                    }
                    if (result.type === 'description') {
                        this.localLocationDrawing.description = result.data.description
                    }
                })

                this.localLocationDrawing.updatedAt = crypto['randomUUID']()

                this.$emit('close', this.localLocationDrawing)
            } catch (error) {
                this.hasError = true
            } finally {
                this.loading = false
            }
        },

        async createLocationDrawing() {
            try {
                this.loading = true

                let data = await projectClient.mutate({
                    mutation: CREATE_LOCATION_DRAWING_MUTATION,
                    variables: {
                        projectId: this.projectId,
                        file: this.availableFields.file,
                        name: this.availableFields.name,
                    },
                })

                this.localLocationDrawing = data.data.createLocationDrawing.locationDrawing

                if (this.availableFields.description) await this.updateLocationDrawing()

                if (this.localLocationDrawing && !this.hasError)
                    this.$emit('close', this.localLocationDrawing)
            } catch (error) {
                this.localLocationDrawing = null
                this.hasError = true
            } finally {
                this.loading = false
            }
        },
    },

    mounted() {
        if (this.locationDrawing) {
            this.localLocationDrawing = this.lodash.cloneDeep(this.locationDrawing)

            Object.keys(this.availableFields).forEach((key) => {
                this.availableFields[key] = this.locationDrawing[key]
            })

            this.editorId = crypto['randomUUID']()
        }
    },
})

export default LocationDrawingDialog
